import ObjectCreationValidator from '@/entities/validators/ObjectCreationValidator'
import { ChipsColorI } from '@/typescript/types/ui-components/BaseChipsI'
import {
  DashboardBillingCycleI,
  DashboardServiceCoreI,
  DashboardStatusI,
} from '@/modules/myServices/types/api/DashboardServices'
import { formatDate } from '@/utils'

export const STATUS_LABELS: Record<DashboardStatusI, string> = {
  active: 'Active',
  pending: 'Pending',
  pending_registration: 'Pending Registration',
  pending_transfer: 'Pending Transfer',
  suspended: 'Suspended',
  terminated: 'Terminated',
  cancelled: 'Cancelled',
  completed: 'Completed',
  expired: 'Expired',
  transferred_away: 'Transferred Away',
  fraud: 'Fraud',
  grace: 'Grace',
  redemption: 'Redemption',
  unknown: 'Unknown',
}

const STATUS_TONES: Record<DashboardStatusI, ChipsColorI> = {
  active: 'green',
  pending: 'orange',
  pending_registration: 'orange',
  pending_transfer: 'orange',
  suspended: 'pink',
  terminated: 'dark_orange',
  cancelled: 'dark_blue',
  completed: 'blue',
  expired: 'pink',
  transferred_away: 'dark_orange',
  fraud: 'red',
  grace: 'pink',
  redemption: 'pink',
  unknown: 'gray',
}

const CYCLE_SUFFIXES: Record<DashboardBillingCycleI, string | null> = {
  monthly: '/ month',
  quarterly: '/ quarter',
  semi_annually: '/ 6 months',
  annually: '/ year',
  biennially: '/ 2 years',
  triennially: '/ 3 years',
  one_time: 'One Time',
  pay_as_you_go: null,
  unknown: null,
}

export default class DashboardServiceItem {
  id: number
  name: string
  status: DashboardStatusI
  billingCycle: DashboardBillingCycleI
  amount: string
  registeredAt: string | null
  nextDueAt: string | null
  /** From `meta.currency` — every amount in the payload is denominated in it. */
  currency: string

  constructor(service: DashboardServiceCoreI, currency: string) {
    this.id = service.id
    this.name = service.name ?? '-'
    this.status = service.status ?? 'unknown'
    this.billingCycle = service.billingCycle ?? 'unknown'
    this.amount = service.amount ?? '-'
    this.registeredAt = service.registeredAt ?? null
    this.nextDueAt = service.nextDueAt ?? null
    this.currency = currency

    ObjectCreationValidator.validate(this)
  }

  get statusLabel(): string {
    return STATUS_LABELS[this.status] ?? STATUS_LABELS.unknown
  }

  get statusTone(): ChipsColorI {
    return STATUS_TONES[this.status] ?? STATUS_TONES.unknown
  }

  get isPending(): boolean {
    return this.status === 'pending'
  }

  get registeredAtLabel(): string {
    if (this.isPending) return ' - '
    return formatDate(this.registeredAt)
  }

  get nextDueAtLabel(): string {
    if (this.isPending) return ' - '
    return formatDate(this.nextDueAt)
  }

  get priceLabel(): string {
    if (this.billingCycle === 'pay_as_you_go') return 'Pay-as-you-go'

    const suffix = CYCLE_SUFFIXES[this.billingCycle]
    if (suffix === null || suffix === undefined) return '-'

    return `${this.currency} ${this.amount} ${suffix}`
  }

  get isManageDisabled(): boolean {
    return this.status === 'terminated' || this.status === 'cancelled'
  }
}
